import {
  Box,
  Button,
  Input,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useMutation, useQuery, useApolloClient } from "@apollo/client";
import { GET_ALL_INVENTORY } from "../../graphql/queries/user";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import _debounce from "lodash.debounce";
import { UPDATE_INVENTORY_DATA_MUTATION } from "../../graphql/mutation/user";
import StagedWithDestinationModal from "./StagedWithDestinationModal";
import ScannerModal from "./ScannerModal";
import AddCommentsModal from "./AddCommentsModal";
import Snackbar from "../../components/Snackbar";

const baseColumns = [
  { field: "boxNumber", headerName: "Box Number" },
  { field: "productCode", headerName: "Product Code" },
  { field: "productName", headerName: "Product Name" },
  { field: "id", headerName: "ID" },
  { field: "dispatchDate", headerName: "Dispatch Date" },
  { field: "trackingNumber", headerName: "Tracking Number" },
  { field: "carrier", headerName: "Carrier" },
  { field: "quantity", headerName: "Quantity" },
  { field: "batchNumber", headerName: "Batch Number" },
  { field: "expireDate", headerName: "Expire Date" },
  { field: "storageUnit", headerName: "Storage Unit" },
  { field: "status", headerName: "Status" },
  { field: "receivedDate", headerName: "Received Date" },
  { field: "stagedDate", headerName: "Staged Date" },
  { field: "emptiedDate", headerName: "Emptied Date" },
  { field: "quarantineDate", headerName: "Quarantine Date" },
  { field: "destroyedDate", headerName: "Destroyed Date" },
  { field: "productTitle", headerName: "Product Title" },
  { field: "comments", headerName: "Comments" },
  { field: "destination", headerName: "Destination" },
  { field: "packType", headerName: "Pack Type" },
  { field: "cycleCount", headerName: "Cycle Count" },
  { field: "cycleCountDate", headerName: "Cycle Count Date" },
  { field: "reserved", headerName: "Reserved" },
];

// Lazily created, reused canvas context for measuring text width in the
// DataGrid's default cell font; falls back to an approximation when canvas
// is unavailable (e.g. jsdom in tests).
let measureCtx;
const measureTextWidth = (text) => {
  if (measureCtx === undefined) {
    measureCtx = document.createElement("canvas").getContext("2d");
    if (measureCtx) {
      measureCtx.font = "400 14px Roboto, Helvetica, Arial, sans-serif";
    }
  }
  return measureCtx ? measureCtx.measureText(text).width : text.length * 8;
};

const InventoryTable = ({ onScannerModeChange }) => {
  const [searchProductCodeTerm, setSearchProductCodeTerm] = useState("");
  const client = useApolloClient();
  const [queryFilters, setQueryFilters] = useState({
    productCode: "",
  });

  const prevIsScannerModeRef = useRef(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalType, setModalType] = useState(null);
  const [openToaster, setOpenToaster] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [scannerType, setScannerType] = useState(null);
  const [isScannerMode, setIsScannerMode] = useState(false);

  const { data: inventoryData, loading: inventoryLoading } = useQuery(
    GET_ALL_INVENTORY,
    {
      variables: {
        input: {
          filters: queryFilters,
        },
      },
      fetchPolicy: "cache-and-network",
    },
  );
  const [updateInventoryData, { loading }] = useMutation(
    UPDATE_INVENTORY_DATA_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_ALL_INVENTORY,
          variables: {
            input: {
              filters: queryFilters,
            },
          },
        },
      ],
    },
  );
  const { getAdminInventory = [] } = inventoryData || {};

  const updatedAdminInventory = getAdminInventory.map((item) => {
    return {
      ...item,
      reserved: item.reserved ? "YES" : "NO",
    };
  });

  // Size the Product Name column to fit its longest value (DataGrid v5 has
  // no autosize API, so names would otherwise be truncated with an ellipsis).
  const columns = useMemo(() => {
    const maxWidth = getAdminInventory.reduce(
      (max, row) =>
        Math.max(max, measureTextWidth(String(row.productName || ""))),
      0,
    );
    // +40 covers cell padding, letter-spacing, and font-fallback drift;
    // 200 keeps the header readable.
    const productNameWidth = Math.max(200, Math.ceil(maxWidth) + 40);
    return baseColumns.map((col) =>
      col.field === "productName" ? { ...col, width: productNameWidth } : col,
    );
  }, [getAdminInventory]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSetQueryFilters = useCallback(
    _debounce((productCode) => {
      setQueryFilters((prevState) => ({
        ...prevState,
        productCode,
      }));
    }, 300),
    [],
  );

  useEffect(() => {
    debounceSetQueryFilters(searchProductCodeTerm);
  }, [searchProductCodeTerm, debounceSetQueryFilters]);

  useEffect(() => {
    if (onScannerModeChange) {
      onScannerModeChange(isScannerMode);
    }
    // Only refetch when switching from scan to normal mode
    if (prevIsScannerModeRef.current && !isScannerMode) {
      client.refetchQueries({
        include: [
          {
            query: GET_ALL_INVENTORY,
            variables: {
              input: {
                filters: queryFilters,
              },
            },
          },
        ],
      });
    }
    prevIsScannerModeRef.current = isScannerMode;
  }, [isScannerMode, onScannerModeChange, client, queryFilters]);

  const handleRowSelect = (ids) => {
    const id = ids[ids.length - 1];
    const rows = updatedAdminInventory.filter((i) => i.id === id);
    setSelectedRows(rows);
  };

  const handleAction = async (status) => {
    await updateInventoryData({
      variables: {
        input: {
          ids: selectedRows.map((i) => i.id),
          status,
        },
      },
    });
    setSelectedRows([]);
  };

  const handleUpdateReserved = async (reservedStatus) => {
    await updateInventoryData({
      variables: {
        input: {
          ids: selectedRows.map((i) => i.id),
          reserved: reservedStatus,
        },
      },
    });
    setSelectedRows([]);
  };

  const handleActionCycleCount = async () => {
    await updateInventoryData({
      variables: {
        input: {
          ids: selectedRows.map((i) => i.id),
          cycleCount: "YES",
        },
      },
    });
    setSelectedRows([]);
  };

  const handleActionModal = (modalType) => {
    setModalType(modalType);
  };

  const handleOpenToaster = (message) => {
    setOpenToaster(true);
    setToasterMessage(message);
  };

  const handleCloseToaster = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setToasterMessage("");
    setOpenToaster(false);
  };

  const handleScannerModal = (scannerType) => {
    setScannerType(scannerType);
  };

  return (
    <Box
      sx={{
        height: "100%",
        padding: "12px 0",
      }}
    >
      <Snackbar
        open={openToaster}
        handleClose={handleCloseToaster}
        message={toasterMessage}
      />
      {modalType === "Staged With Destination" ||
      modalType === "Emptied With Destination" ? (
        <StagedWithDestinationModal
          open={true}
          handleClose={() => {
            setModalType(null);
            setSelectedRows([]);
          }}
          handleOpenToaster={handleOpenToaster}
          status={
            modalType === "Staged With Destination" ? "Staged" : "Emptied"
          }
          modalTitle={modalType}
          selectedRows={selectedRows}
          queryFilters={queryFilters}
        />
      ) : null}
      {modalType === "Add Comments" ? (
        <AddCommentsModal
          open={true}
          handleClose={() => {
            setModalType(null);
            setSelectedRows([]);
          }}
          handleOpenToaster={handleOpenToaster}
          selectedRows={selectedRows}
          queryFilters={queryFilters}
        />
      ) : null}
      {scannerType ? (
        <ScannerModal
          open={true}
          handleClose={() => {
            setScannerType(null);
          }}
          handleOpenToaster={handleOpenToaster}
          scannerType={scannerType}
        />
      ) : null}
      <Box
        sx={{
          display: "flex",
          justifyContent: isScannerMode ? "center" : "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        {!isScannerMode && (
          <Input
            placeholder="Search by box number, product code, or name"
            value={searchProductCodeTerm}
            defaultChecked={searchProductCodeTerm}
            onChange={(e) => {
              const { value } = e.target;
              setSearchProductCodeTerm(value);
            }}
            name="productCode"
            type="search"
            sx={{ width: "350px" }}
          />
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: isScannerMode ? 400 : 600 }}
          >
            Normal Mode
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={isScannerMode}
                onChange={(e) => setIsScannerMode(e.target.checked)}
                color="primary"
              />
            }
            label=""
            sx={{ m: 0 }}
          />
          <Typography
            variant="body1"
            sx={{ fontWeight: isScannerMode ? 600 : 400 }}
          >
            Scanner Mode
          </Typography>
        </Box>
      </Box>

      {!isScannerMode ? (
        <>
          {/* Row 1: Standard action buttons */}
          <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <Button
              onClick={() => handleAction("Received")}
              variant="contained"
              disabled={
                !selectedRows?.length ||
                inventoryLoading ||
                loading ||
                selectedRows?.[0]?.status === "Received"
              }
            >
              Mark Received
            </Button>
            <Button
              onClick={() => handleAction("Staged")}
              variant="contained"
              disabled={
                !selectedRows?.length ||
                inventoryLoading ||
                loading ||
                selectedRows?.[0]?.status === "Staged"
              }
            >
              Mark Staged
            </Button>
            <Button
              onClick={() => handleAction("Emptied")}
              variant="contained"
              disabled={!selectedRows?.length || inventoryLoading || loading}
            >
              Mark Emptied
            </Button>
            <Button
              onClick={() => handleAction("Quarantined")}
              variant="contained"
              disabled={!selectedRows?.length || inventoryLoading || loading}
            >
              Mark Quarantined
            </Button>
            <Button
              onClick={() => handleAction("Destroyed")}
              variant="contained"
              disabled={!selectedRows?.length || inventoryLoading || loading}
            >
              Mark Destroyed
            </Button>
            <Button
              onClick={() => handleActionCycleCount()}
              variant="contained"
              disabled={
                !selectedRows?.length ||
                inventoryLoading ||
                loading ||
                selectedRows?.[0]?.cycleCount === "YES"
              }
            >
              Mark Cycle Count
            </Button>
          </Box>

          {/* Row 2: Destination-based buttons */}
          <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <Button
              onClick={() => handleActionModal("Staged With Destination")}
              variant="contained"
              color="secondary"
              disabled={
                !selectedRows?.length ||
                inventoryLoading ||
                loading ||
                selectedRows?.[0]?.status === "Staged"
              }
            >
              Mark Staged with Destination
            </Button>
            <Button
              onClick={() => handleActionModal("Emptied With Destination")}
              variant="contained"
              color="secondary"
              disabled={!selectedRows?.length || inventoryLoading || loading}
            >
              Mark Emptied with Destination
            </Button>
            <Button
              onClick={() =>
                handleUpdateReserved(
                  selectedRows[0]?.reserved === "YES" ? false : true,
                )
              }
              variant="contained"
              color="secondary"
              disabled={!selectedRows?.length || inventoryLoading || loading}
            >
              {selectedRows[0]?.reserved === "YES"
                ? "Mark Unreserved"
                : "Mark Reserved"}
            </Button>
            <Button
              onClick={() => setModalType("Add Comments")}
              variant="contained"
              color="secondary"
              disabled={!selectedRows?.length || inventoryLoading || loading}
            >
              Add Comments
            </Button>
          </Box>
        </>
      ) : (
        <>
          {/* Scanner Mode Buttons - Optimized for Mobile */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mb: 2,
              maxWidth: "600px",
              margin: "0 auto",
              padding: "0 16px",
            }}
          >
            <Button
              onClick={() => handleScannerModal("RECEIVED")}
              variant="contained"
              size="large"
              sx={{
                width: "100%",
                minHeight: "60px",
                fontSize: "1.1rem",
                fontWeight: 600,
              }}
            >
              Mark Received
            </Button>
            <Button
              onClick={() => handleScannerModal("STAGED")}
              variant="contained"
              size="large"
              sx={{
                width: "100%",
                minHeight: "60px",
                fontSize: "1.1rem",
                fontWeight: 600,
              }}
            >
              Mark Staged
            </Button>
            <Button
              onClick={() => handleScannerModal("EMPTIED")}
              variant="contained"
              size="large"
              sx={{
                width: "100%",
                minHeight: "60px",
                fontSize: "1.1rem",
                fontWeight: 600,
              }}
            >
              Mark Emptied
            </Button>
            <Button
              onClick={() => handleScannerModal("QUARANTINED")}
              variant="contained"
              size="large"
              sx={{
                width: "100%",
                minHeight: "60px",
                fontSize: "1.1rem",
                fontWeight: 600,
              }}
            >
              Mark Quarantined
            </Button>
            <Button
              onClick={() => handleScannerModal("CYCLE_COUNT")}
              variant="contained"
              size="large"
              sx={{
                width: "100%",
                minHeight: "60px",
                fontSize: "1.1rem",
                fontWeight: 600,
              }}
            >
              Mark Cycle Count
            </Button>
          </Box>
        </>
      )}
      {!isScannerMode && (
        <DataGrid
          sx={{
            marginTop: "8px",
            "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer":
              {
                display: "none",
              },
          }}
          loading={inventoryLoading || loading}
          rows={updatedAdminInventory}
          columns={columns}
          pageSize={50}
          rowsPerPageOptions={[50]}
          onSelectionModelChange={handleRowSelect}
          checkboxSelection
          selectionModel={selectedRows.map((i) => i.id)}
        />
      )}
    </Box>
  );
};

export default InventoryTable;
